import React, { useEffect, useState } from 'react';
import { KeyRound, ShieldCheck, X } from 'lucide-react';
import api from '../../services/api';
import alertService from '../../utils/alert';
import PageHeader from '../../components/PageHeader';

const PROVIDER_LABELS = {
  stripe: 'Stripe',
  razorpay: 'Razorpay',
  exotel: 'Exotel',
  twilio_sms: 'Twilio (SMS)',
  openai: 'OpenAI (AI Support Agent)',
  anthropic: 'Anthropic (Claude)',
  sarvam: 'Sarvam AI (Hindi voice)',
  linkedin_app: 'LinkedIn app (used by every workspace)',
  google_drive_app: 'Google Drive App',
  dropbox_app: 'Dropbox App',
  box_app: 'Box App',
  onedrive_app: 'OneDrive App',
};

// Which fields each provider needs, matching PlatformCredentialStore/CredentialStore's
// per-provider _runTest expectations exactly (apiKey/webhookSecret for stripe,
// keyId/keySecret for razorpay, sid/apiKey/apiToken/subdomain for exotel, etc.).
const PROVIDER_FIELDS = {
  stripe: [{ name: 'apiKey', label: 'Secret Key' }, { name: 'webhookSecret', label: 'Webhook Secret' }],
  razorpay: [{ name: 'keyId', label: 'Key ID' }, { name: 'keySecret', label: 'Key Secret' }],
  exotel: [
    { name: 'sid', label: 'Account SID' },
    { name: 'apiKey', label: 'API Key' },
    { name: 'apiToken', label: 'API Token' },
    { name: 'subdomain', label: 'Subdomain (default: api)' },
  ],
  twilio_sms: [{ name: 'accountSid', label: 'Account SID' }, { name: 'authToken', label: 'Auth Token' }],
  openai: [
    { name: 'apiKey', label: 'API Key' },
    { name: 'baseURL', label: 'Base URL (optional — for Azure/OpenAI-compatible proxies)' },
  ],
  anthropic: [
    { name: 'apiKey', label: 'API Key' },
    { name: 'workspaceId', label: 'Workspace ID (optional — only if the key is not scoped to a workspace)' },
  ],
  sarvam: [{ name: 'apiKey', label: 'API Key' }],
  linkedin_app: [
    { name: 'clientId', label: 'Client ID' },
    { name: 'clientSecret', label: 'Client Secret' },
    { name: 'redirectUri', label: 'Redirect URI (optional — default is this site\'s /api/linkedin/callback)' },
    { name: 'scopes', label: 'Scopes (optional — default includes organization scopes)' },
  ],
  google_drive_app: [{ name: 'clientId', label: 'Client ID' }, { name: 'clientSecret', label: 'Client Secret' }],
  dropbox_app: [{ name: 'clientId', label: 'App Key' }, { name: 'clientSecret', label: 'App Secret' }],
  box_app: [{ name: 'clientId', label: 'Client ID' }, { name: 'clientSecret', label: 'Client Secret' }],
  onedrive_app: [{ name: 'clientId', label: 'Client ID' }, { name: 'clientSecret', label: 'Client Secret' }, { name: 'tenantId', label: 'Azure Tenant ID' }],
};

const STATUS_STYLES = {
  ok: 'bg-[#EBF7E6] text-[#2E844A] border-[#CDEFC4]',
  error: 'bg-[#FEF1EE] text-[#BA0517] border-[#FDDDE3]',
  untested: 'bg-[#F7F9FB] text-[#706E6B] border-[#E5E5E5]',
};

export default function SuperAdminPlatformKeys() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // provider name
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [testingProvider, setTestingProvider] = useState(null);

  const load = () => {
    setLoading(true);
    api.get('/admin/platform-keys')
      .then(res => setProviders(res.data.providers || []))
      .catch(() => alertService.toast('error', 'Failed to load platform keys'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openEdit = async (provider) => {
    setEditing(provider);
    setForm({});
    try {
      const res = await api.get(`/admin/platform-keys/${provider}`);
      // Masked values are shown as placeholders only — never re-submitted unless changed.
      const blank = {};
      (PROVIDER_FIELDS[provider] || []).forEach(f => { blank[f.name] = ''; });
      setForm(blank);
      if (res.data.credentials) {
        setForm(prev => ({ ...prev, __masked: res.data.credentials }));
      }
    } catch {
      // no existing credential — blank form is fine
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { __masked, ...credentials } = form;
      // Only send fields the admin actually typed a new value into.
      const payload = {};
      for (const [k, v] of Object.entries(credentials)) {
        if (v) payload[k] = v;
      }
      await api.put(`/admin/platform-keys/${editing}`, { credentials: payload });
      alertService.toast('success', 'Platform key saved');
      setEditing(null);
      load();
    } catch (err) {
      alertService.toast('error', err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const test = async (provider) => {
    setTestingProvider(provider);
    try {
      const res = await api.post(`/admin/platform-keys/${provider}/test`);
      alertService.toast(res.data.ok ? 'success' : 'error', res.data.ok ? 'Connection succeeded' : (res.data.error || 'Connection failed'));
      load();
    } catch (err) {
      alertService.toast('error', 'Test failed');
    } finally {
      setTestingProvider(null);
    }
  };

  if (loading) return <div className="p-8 text-sm text-[#706E6B]">Loading…</div>;

  return (
    <div className="p-8 max-w-4xl">
      <PageHeader title="Platform Integrations" icon={KeyRound} subtitle={'The SaaS\'s own provider credentials — encrypted at rest, superadmin-only. Distinct from tenant API keys under "API Keys."'} />

      <div className="space-y-3">
        {providers.map(p => (
          <div key={p.provider} className="border border-[#E5E5E5] rounded-lg p-4 flex items-center justify-between bg-white shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
            <div>
              <div className="font-semibold text-[#080707]">{PROVIDER_LABELS[p.provider] || p.provider}</div>
              <div className="flex items-center gap-2 mt-1">
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs border ${p.isActive ? (STATUS_STYLES[p.lastTestStatus] || STATUS_STYLES.untested) : 'bg-[#F7F9FB] text-[#706E6B] border-[#E5E5E5]'}`}>
                  {p.isActive ? (p.lastTestStatus || 'untested') : 'not configured'}
                </span>
                {p.lastTestedAt && <span className="text-xs text-[#706E6B]">tested {new Date(p.lastTestedAt).toLocaleDateString()}</span>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {p.isActive && (
                <button
                  onClick={() => test(p.provider)}
                  disabled={testingProvider === p.provider}
                  className="px-3 py-1.5 text-[13px] font-semibold border border-[#E5E5E5] rounded hover:bg-[#F7F9FB] flex items-center gap-1.5"
                >
                  <ShieldCheck size={13} /> Test
                </button>
              )}
              <button
                onClick={() => openEdit(p.provider)}
                className="px-3 py-1.5 text-[13px] font-semibold bg-[#0176D3] text-white rounded hover:bg-[#014486]"
              >
                {p.isActive ? 'Update' : 'Configure'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4 bg-black/40" onClick={() => setEditing(null)}>
          <div className="bg-white w-full max-w-md p-4 sm:p-6 rounded-t-lg sm:rounded-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[16px] font-bold text-[#2E2E2E]">{PROVIDER_LABELS[editing]}</h3>
              <button onClick={() => setEditing(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              {(PROVIDER_FIELDS[editing] || []).map(f => (
                <div key={f.name}>
                  <label className="block text-[11px] font-semibold text-[#706E6B] mb-1">{f.label}</label>
                  <input
                    type="password"
                    value={form[f.name] || ''}
                    onChange={e => setForm(prev => ({ ...prev, [f.name]: e.target.value }))}
                    placeholder={form.__masked?.[f.name] || 'Not set'}
                    className="w-full px-3 py-2 border border-[#D8DDE6] rounded text-[13px]"
                  />
                </div>
              ))}
              <p className="text-xs text-[#706E6B]">Leave a field blank to keep its current value unchanged.</p>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setEditing(null)} className="h-9 px-4 bg-white border border-[#C9C9C9] text-[#0176D3] text-[13px] font-semibold rounded hover:bg-[#F3F2F2]">Cancel</button>
                <button onClick={save} disabled={saving} className="hover:bg-[#014486] px-4 py-2 text-[13px] font-semibold bg-[#0176D3] text-white rounded whitespace-nowrap disabled:opacity-60">
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
